import styled from '@emotion/styled';
import { COLORS } from 'styles/constants';
import { Divider, Popover, Tooltip } from '@mui/material';
import { useState } from 'react';
import { OutTaskboxDetailResponse } from 'queries/model';
import { Icons } from 'components';
import { ConfirmDeleteDialog } from './ConfirmDeleteDialog';
import { osName } from 'react-device-detect';
import { InboxContextMenuType } from 'components/InboxContextMenu';
import { useKeyboardEvent } from '@react-hookz/web';

interface Props {
  event?: OutTaskboxDetailResponse;
  detail?: HTMLElement | null;
  focusedTaskbox?: OutTaskboxDetailResponse;
  suppressDivider?: boolean;
  onCloseDetail?: () => void;
  onClickMenu?: (menu: InboxContextMenuType, e?: React.MouseEvent<HTMLDivElement, MouseEvent>) => void;
}

export const TimelineTaskboxDetail = ({ event, detail, focusedTaskbox, suppressDivider = false, onCloseDetail = () => {}, onClickMenu = () => {} }: Props) => {
  const [removeTaskbox, setRemoveTaskbox] = useState(false);

  useKeyboardEvent(
    true,
    (ev) => {
      if ((ev.code === 'Delete' && osName === 'Windows') || (ev.code === 'Backspace' && ev.metaKey && osName === 'Mac OS')) {
        if (focusedTaskbox) return;
        handleRemoveTaskbox();
      }
    },
    [],
    { eventOptions: { passive: true } },
  );

  const handleRemoveTaskbox = () => {
    if (event?.project) {
      onClickMenu('DELETE');
    } else {
      setRemoveTaskbox(true);
    }
  };

  const handleClickConfirmDelete = () => {
    setRemoveTaskbox(false);
    onClickMenu('DELETE');
  };

  return (
    <>
      <Popover
        open={Boolean(detail)}
        anchorEl={detail}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        onClose={onCloseDetail}
        sx={{ marginTop: '4px' }}
      >
        <DetailModal>
          <DetailModalOption
            className="detail-text"
            onClick={() => {
              onClickMenu('CATEGORY');
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Icons.Category />
              <span style={{ marginLeft: '8px' }}>카테고리 추가하기</span>
            </div>
          </DetailModalOption>
          <DetailModalOption
            className="detail-text"
            onClick={() => {
              onClickMenu('DEADLINE');
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <Icons.Flag width={16} height={16} fill={COLORS.gray900} />
              <span style={{ marginLeft: '8px' }}>기한 추가하기</span>
            </div>
          </DetailModalOption>
          {!suppressDivider && <Divider sx={{ width: '100%', margin: '4px 0px' }} />}
          {!event?.project && (
            <DetailModalOption
              className="detail-text"
              onClick={() => {
                onClickMenu('DUPLICATE');
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Icons.Duplicate />
                <span style={{ marginLeft: '8px' }}>태스크박스 복제하기</span>
              </div>
            </DetailModalOption>
          )}
          {!event?.project && (
            <>
              {event!.tasks!.length > 0 ? (
                <DetailModalOption
                  className="detail-text"
                  onClick={() => {
                    onClickMenu('TEMPLATE');
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center' }}>
                    <Icons.Template />
                    <span style={{ marginLeft: '8px' }}>템플릿으로 저장하기</span>
                  </div>
                </DetailModalOption>
              ) : (
                <Tooltip title={'템플릿으로 저장할 하위 업무가 없어요.'} disableInteractive>
                  <DetailModalOption className="detail-text" style={{ cursor: 'default' }}>
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                      <Icons.Template />
                      <span style={{ marginLeft: '8px' }}>템플릿으로 저장하기</span>
                    </div>
                  </DetailModalOption>
                </Tooltip>
              )}
            </>
          )}
          {!suppressDivider && <Divider sx={{ width: '100%', margin: '4px 0px' }} />}
          {!event?.project && !event?.isRecurrence && (
            <DetailModalOption
              className="detail-text"
              onClick={() => {
                onClickMenu('CONVERT_TO_PROJECT');
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Icons.ConvertToProject />
                <span style={{ marginLeft: '8px' }}>새로운 프로젝트로 전환하기</span>
              </div>
            </DetailModalOption>
          )}
          {!event?.project && !event?.isRecurrence && (
            <DetailModalOption
              className="detail-text"
              onClick={(e) => {
                onClickMenu('LINK_TO_PROJECT', e);
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center' }}>
                <Icons.LinkToProject />
                <span style={{ marginLeft: '8px' }}>기존 프로젝트에 연결하기</span>
              </div>
            </DetailModalOption>
          )}
          <DetailModalOption className="detail-text" onClick={handleRemoveTaskbox} style={{ color: `${COLORS.negative1}` }}>
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {event?.project ? <Icons.DeleteInstance /> : <Icons.Delete fill={COLORS.negative1} />}
              <span style={{ marginLeft: '8px', marginRight: '22px' }}>{event?.project ? '인스턴스 제거하기' : '삭제하기'}</span>
            </div>
            {osName === 'Windows' ? (
              <KeyboardButtonRect small>Delete</KeyboardButtonRect>
            ) : (
              <div style={{ color: COLORS.gray500, marginLeft: '8px' }}>
                <KeyboardButtonRect small>⌘</KeyboardButtonRect>
                <KeyboardCommandPlus>+</KeyboardCommandPlus>
                <KeyboardButtonRect small>Backspace</KeyboardButtonRect>
              </div>
            )}
          </DetailModalOption>
        </DetailModal>
      </Popover>
      <ConfirmDeleteDialog open={removeTaskbox} onClose={() => setRemoveTaskbox(false)} onDelete={handleClickConfirmDelete} />
    </>
  );
};

const DetailModal = styled.div`
  min-width: 240px;
  height: fit-content;
  min-width: 138px;
  background-color: ${COLORS.white};
  box-shadow: 0px 8px 16px 0px rgba(26, 30, 39, 0.16);
  border-radius: 8px;
  padding: 8px;
  z-index: 10;
`;

const DetailModalOption = styled.div`
  font-size: 12px;
  padding: 8px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-radius: 8px;
  cursor: pointer;
  &:hover {
    background-color: ${COLORS.gray100};
  }
`;

const KeyboardButtonRect = styled.span<{ small?: boolean }>`
  height: 16px;
  background: #ffffff;
  border: 1px solid ${COLORS.gray400};
  border-radius: 2px;
  font-size: 10px;
  font-weight: 700;
  color: ${COLORS.gray500};
  padding: ${(props) => `${props.small ? '2px 3px' : '4px'}`};
`;

const KeyboardCommandPlus = styled.span`
  font-size: 12px;
  color: ${COLORS.gray500};
  margin: 0px 4px;
`;
